// Application Detection Utility
// Since we can't directly access the file system from the browser,
// we'll use a combination of user agent detection and local storage preferences

export const detectApplications = async () => {
  const applications = {
    r: false,
    rstudio: false,
    sas: false
  };

  try {
    // Check if user has previously indicated these applications are available
    const savedPreferences = localStorage.getItem('availableApplications');
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      return { ...applications, ...preferences };
    }

    // Try to detect based on user agent and other browser capabilities
    const userAgent = navigator.userAgent.toLowerCase();

    // Check for R-related indicators
    if (userAgent.includes('rstudio') || 
        localStorage.getItem('rstudio_available') === 'true') {
      applications.rstudio = true;
      applications.r = true; // RStudio implies R is available
    }

    // Check for SAS-related indicators
    if (userAgent.includes('sas') || 
        localStorage.getItem('sas_available') === 'true') {
      applications.sas = true;
    }

    // Check for R installation (this is a best guess)
    if (localStorage.getItem('r_available') === 'true' || 
        applications.rstudio) {
      applications.r = true;
    }

    // Save preferences for future use
    localStorage.setItem('availableApplications', JSON.stringify(applications));

    return applications;
  } catch (error) {
    console.error('Error detecting applications:', error);
    return applications;
  }
};

export const setApplicationAvailability = (appName, isAvailable) => {
  try {
    const current = JSON.parse(localStorage.getItem('availableApplications') || '{}');
    current[appName] = isAvailable;
    localStorage.setItem('availableApplications', JSON.stringify(current));
  } catch (error) {
    console.error('Error saving application availability:', error);
  }
};

export const getApplicationAvailability = () => {
  try {
    return JSON.parse(localStorage.getItem('availableApplications') || '{}');
  } catch (error) {
    console.error('Error getting application availability:', error);
    return {};
  }
};

// Code formatting utilities
export const formatCodeForApplication = (code, language, targetApp) => {
  const timestamp = new Date().toISOString();
  
  switch (targetApp) {
    case 'r':
    case 'rstudio':
      return formatForR(code, language, timestamp);
    case 'sas':
      return formatForSAS(code, language, timestamp);
    default:
      return code;
  }
};

const formatForR = (code, language, timestamp) => {
  let formattedCode = code;
  
  // Add R-specific header if not already present
  if (!formattedCode.includes('# Generated by TLF Assistant')) {
    formattedCode = `# Generated by TLF Assistant
# Timestamp: ${timestamp}
# Original Language: ${language}
# 
# Instructions:
# 1. Copy this code to R or RStudio
# 2. Make sure required packages are installed
# 3. Run the code
#
# ==========================================

${formattedCode}`;
  }
  
  return formattedCode;
};

const formatForSAS = (code, language, timestamp) => {
  let formattedCode = code;
  
  // Add SAS-specific header if not already present
  if (!formattedCode.includes('/* Generated by TLF Assistant')) {
    formattedCode = `/* Generated by TLF Assistant
   Timestamp: ${timestamp}
   Original Language: ${language}
   
   Instructions:
   1. Copy this code to SAS Studio or SAS Enterprise Guide
   2. Make sure required libraries are available
   3. Run the code
   
   ========================================== */

${formattedCode}`;
  }
  
  return formattedCode;
};

// File extension utilities
export const getFileExtension = (appType) => {
  switch (appType) {
    case 'r':
    case 'rstudio':
      return 'r';
    case 'sas':
      return 'sas';
    default:
      return 'txt';
  }
};

// MIME type utilities
export const getMimeType = (appType) => {
  switch (appType) {
    case 'r':
    case 'rstudio':
      return 'text/plain';
    case 'sas':
      return 'text/plain';
    default:
      return 'text/plain';
  }
};

// Download utilities
export const downloadCode = (code, filename, mimeType) => {
  try {
    const blob = new Blob([code], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading code:', error);
    return false;
  }
};

// Copy to clipboard utility
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

// Application-specific code templates
export const getCodeTemplate = (appType, language, code) => {
  const templates = {
    r: {
      python: `# Converted from Python to R
# Note: Some Python-specific functions may need manual conversion

${code}`,
      sas: `# Converted from SAS to R
# Note: SAS procedures may need to be converted to R functions

${code}`,
      default: code
    },
    sas: {
      r: `/* Converted from R to SAS
   Note: R functions may need to be converted to SAS procedures */

${code}`,
      python: `/* Converted from Python to SAS
   Note: Python-specific functions may need manual conversion */

${code}`,
      default: code
    }
  };
  
  return templates[appType]?.[language] || templates[appType]?.default || code;
};
